import {
  React,
  useState,
  useEffect,
  useNavigate,
  useSelector,
  useLocation,
  useModal,
  Logo,
  Footer,
} from "../../globals/imports";

import HomeBackground from "../../assets/images/home_background.svg";
import ThemeToggleButton from "./../../utils/theme";
import Pricing from "./Pricing";

const HomePage = () => {
  const [activeTab, setActiveTab] = useState("home");
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const myData = useSelector((state) => state.myData);
  const { handleOpenModal } = useModal();
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/pricing") {
      setActiveTab("pricing");
    } else {
      setActiveTab("home");
    }
  }, [location.pathname]);

  const handleLogin = () => {
    setLoading(true);
    if (isLoggedIn) {
      setTimeout(() => {
        if (myData.username) {
          setLoading(false);
          navigate(`/admin/dashboard`);
        }
      }, 500);
    } else {
      setLoading(false);
      navigate("/get-started");
    }
  };

  const handleCreateChannel = () => {
    if (isLoggedIn) {
      if (myData.username) {
        navigate(`/admin/dashboard`);
      }
      setTimeout(() => {
        handleOpenModal("modalChannelOpen");
      }, 500);
    } else {
      navigate("/get-started");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-theme-primaryBackground overflow-x-hidden">
      {/* Background image layer */}
      {activeTab === "home" ? (
        <img
          src={HomeBackground}
          alt="home-background"
          className="fixed object-cover w-full h-full -z-10"
        />
      ) : (
        <div className="bg-[#202020] fixed w-full h-full -z-10"></div>
      )}

      {/* Header/Nav */}
      <div className="flex flex-row justify-between w-full items-center px-6 z-10 mt-2">
        <img src={Logo} alt="logo" className="h-8 w-auto" />

        <div className="space-x-8 mt-2 md:flex hidden">
          <button
            onClick={() => navigate("/")}
            className={`text-sm font-light tracking-wider ${
              activeTab === "home"
                ? "border-b-2 border-white text-white"
                : "text-white"
            } pb-2 px-3 transition-all`}
          >
            Home
          </button>
          <button
            onClick={() => navigate("/pricing")}
            className={`text-sm font-light tracking-wider ${
              activeTab === "pricing"
                ? "border-b-2 border-white text-white"
                : "text-white"
            } pb-2 px-3 transition-all`}
          >
            Pricing
          </button>
        </div>

        <div
          className="border cursor-pointer border-white rounded-md px-6 py-2 text-white font-normal text-sm"
          onClick={handleLogin}
        >
          {loading ? "Loading.." : isLoggedIn ? "Profile" : "Login"}
        </div>
      </div>

      {/* Mobile Nav Tabs */}
      <div className="space-x-8 md:hidden flex z-10 mx-auto mt-10">
        <button
          onClick={() => setActiveTab("home")}
          className={`text-sm font-light tracking-wider ${
            activeTab === "home"
              ? "border-b-2 border-white text-white"
              : "text-white"
          } pb-2 px-3 transition-all`}
        >
          Home
        </button>
        <button
          onClick={() => setActiveTab("pricing")}
          className={`text-sm font-light tracking-wider ${
            activeTab === "pricing"
              ? "border-b-2 border-white text-white"
              : "text-white"
          } pb-2 px-3 transition-all`}
        >
          Pricing
        </button>
      </div>

      {/* Page Content */}
      <div className="flex-grow z-10">
        {activeTab === "home" ? (
          <div className="flex sm:flex-row flex-col items-center justify-start md:justify-between mt-10 lg:ml-20 ml-6 lg:pr-20 md:pr-10 pr-6 h-full">
            <div className="flex flex-col w-full lg:w-1/3 md:w-[45%] sm:w-2/3 space-y-3 sm:mr-0 mr-6">
              <div className="text-left text-theme-sidebarColor xl:text-lg text-sm font-normal font-inter">
                Your people, Your space
              </div>
              <div className="text-white xl:text-3xl text-2xl font-normal font-inter">
                Why need a ton of apps for a great community, when they already
                love yours
              </div>
              <div className="opacity-80 text-white xl:text-sm text-xs font-light font-inter">
                Simply integrate chats, events, and share content with your
                audience right inside your website or app, giving your community
                a space to thrive.
              </div>
              <div className="flex flex-row justify-start items-center pt-2">
                <div
                  className="bg-white cursor-pointer rounded-lg lg:px-5 px-4 sm:px-2 xl:py-3 py-2 text-black text-sm"
                  onClick={handleCreateChannel}
                >
                  Create your Channel
                </div>
                <a
                  href="https://calendly.com/channels_social/talk-to-us"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border lg:ml-5 sm:ml-3 ml-5 cursor-pointer border-white rounded-md lg:px-4 sm:px-2 px-4 xl:py-3 py-2 text-white font-normal text-sm"
                >
                  Talk to us
                </a>
              </div>
            </div>

            {/* D2C mockup: the Channels embed living inside a customer's site.
                Self-contained (no image assets) so it always renders crisply. */}
            <div className="sm:ml-8 sm:mt-0 mt-8 lg:w-3/5 md:w-[45%] sm:w-2/3 w-full sm:mr-0 mr-6">
              <div className="overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
                {/* browser chrome */}
                <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-100 px-4 py-2.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
                  <div className="ml-3 flex-1 truncate rounded-md bg-white px-3 py-1 text-[11px] text-gray-400">
                    yourstore.com
                  </div>
                </div>
                {/* embedded community */}
                <div className="flex h-[300px] sm:h-[340px]">
                  {/* channel rail */}
                  <div className="w-1/3 border-r border-gray-100 bg-gray-50 p-3">
                    <div className="mb-3 flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#202020] text-[10px] font-bold text-white">
                        A
                      </span>
                      <span className="truncate text-xs font-semibold text-gray-800">
                        Acme Community
                      </span>
                    </div>
                    {["# general", "# help", "# events", "# launches"].map(
                      (c, i) => (
                        <div
                          key={c}
                          className={`mb-1 truncate rounded-md px-2 py-1.5 text-xs ${
                            i === 0
                              ? "bg-[#202020] text-white"
                              : "text-gray-500"
                          }`}
                        >
                          {c}
                        </div>
                      )
                    )}
                  </div>
                  {/* chat */}
                  <div className="flex w-2/3 flex-col">
                    <div className="border-b border-gray-100 px-4 py-2.5 text-xs font-semibold text-gray-800">
                      # general
                    </div>
                    <div className="flex-1 space-y-3 overflow-hidden p-4">
                      {[
                        {
                          n: "Maya",
                          c: "bg-[#FBB28B]",
                          t: "just joined — loving the new drop! 🔥",
                        },
                        {
                          n: "Devon",
                          c: "bg-[#96D7FF]",
                          t: "any restock on the navy hoodie?",
                        },
                        {
                          n: "Team",
                          c: "bg-[#B2EE8F]",
                          t: "dropping Friday — members get early access ✨",
                        },
                      ].map((m) => (
                        <div key={m.n} className="flex items-start gap-2">
                          <span
                            className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-gray-800 ${m.c}`}
                          >
                            {m.n[0]}
                          </span>
                          <div>
                            <div className="text-[11px] font-semibold text-gray-700">
                              {m.n}
                            </div>
                            <div className="mt-0.5 rounded-lg rounded-tl-none bg-gray-100 px-3 py-1.5 text-[11px] text-gray-600">
                              {m.t}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-gray-100 p-3">
                      <div className="flex items-center rounded-full border border-gray-200 px-3 py-1.5 text-[11px] text-gray-400">
                        Message #general…
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === "pricing" ? (
          <Pricing />
        ) : (
          <div></div>
        )}
      </div>
      <div className="h-4"></div>

      {/* Footer (natural position) */}
      <Footer />
    </div>
  );
};

export default HomePage;
